import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { FileText, Search, Download, Eye, Upload, Plus, Settings } from 'lucide-react';

function MachineDrawings() {
  const { API } = useContext(AppContext);
  const [drawings, setDrawings] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMachine, setSelectedMachine] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [drawingsRes, machineryRes] = await Promise.all([
        axios.get(`${API}/maintenance/drawings`),
        axios.get(`${API}/maintenance/machinery`)
      ]);
      setDrawings(drawingsRes.data);
      setMachinery(machineryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Assembly': return 'bg-blue-100 text-blue-700';
      case 'Electrical': return 'bg-yellow-100 text-yellow-700';
      case 'Layout': return 'bg-green-100 text-green-700';
      case 'Hydraulic': return 'bg-purple-100 text-purple-700';
      case 'P&ID': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredDrawings = drawings.filter(d => {
    const matchesMachine = selectedMachine === 'all' || d.machineId === selectedMachine;
    const matchesSearch = d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          d.machineName.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesMachine && matchesSearch;
  });

  const uniqueMachines = [...new Set(drawings.map(d => ({ id: d.machineId, name: d.machineName })))];

  if (loading) {
    return <div className="text-center py-12">Loading drawings...</div>;
  }

  return (
    <div className="space-y-6" data-testid="machine-drawings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Machine Drawings</h1>
          <p className="text-gray-500 mt-1">Technical drawings and documentation for all machinery</p>
        </div>
        <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md">
          <Upload size={18} />
          <span>Upload Drawing</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-2">
            <FileText className="text-[#1a237e]" size={18} />
            <p className="text-sm text-gray-500">Total Drawings</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{drawings.length}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Assembly</p>
          <p className="text-2xl font-bold text-blue-700">{drawings.filter(d => d.type === 'Assembly').length}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-5 border border-yellow-200">
          <p className="text-sm text-yellow-700 font-medium">Electrical</p>
          <p className="text-2xl font-bold text-yellow-700">{drawings.filter(d => d.type === 'Electrical').length}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600 font-medium">Layout</p>
          <p className="text-2xl font-bold text-green-700">{drawings.filter(d => d.type === 'Layout').length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <select
          value={selectedMachine}
          onChange={(e) => setSelectedMachine(e.target.value)}
          className="px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none bg-white"
        >
          <option value="all">All Machines</option>
          {machinery.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search drawings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
          />
        </div>
      </div>

      {/* Drawings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDrawings.map((drawing) => (
          <div key={drawing.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-gray-100 rounded-lg">
                    <FileText className="text-[#1a237e]" size={24} />
                  </div>
                  <div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(drawing.type)}`}>
                      {drawing.type}
                    </span>
                    <h3 className="text-sm font-semibold text-gray-900 mt-1">{drawing.title}</h3>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center space-x-2 text-sm">
                  <Settings size={14} className="text-gray-400" />
                  <span className="text-gray-600">{drawing.machineName}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Version</span>
                  <span className="font-mono text-[#1a237e]">{drawing.version}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Uploaded</span>
                  <span className="text-gray-600">{drawing.uploadDate}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Format</span>
                  <span className="font-medium text-gray-900">{drawing.fileType}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">By: {drawing.uploadedBy}</span>
                <div className="flex items-center space-x-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="View">
                    <Eye size={16} className="text-gray-500" />
                  </button>
                  <button className="p-2 hover:bg-blue-100 rounded-lg transition-colors" title="Download">
                    <Download size={16} className="text-blue-500" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredDrawings.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No drawings found matching your criteria</p>
        </div>
      )}
    </div>
  );
}

export default MachineDrawings;
